'use client';

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { ReferenceTariffType } from '@/app/types/reference-tariff-type';
import {
  CustomerType,
  CustomerTypeDefaults,
} from '@/lib/offer-service/types/customer-type';

export interface CalculatorData {
  consumption?: number;
  size?: string;
  zipCode?: string;
  city?: string;
  street?: string;
  houseNumber?: string;
  gridOperator?: string;
  gridOperatorId?: number;
  gridAreaId?: number;
}

export interface EnergyCalculatorState extends CalculatorData {
  setCalculatorData: (value: CalculatorData, mode: ReferenceTariffType) => void;
  bookingLink: string | null;
  selfLink: string | null;
  calculationMode: ReferenceTariffType | null;
  customerType: CustomerType;
  setCustomerType: (value: CustomerType) => void;
}

const useEnergyCalculatorStore = create<EnergyCalculatorState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      consumption: 3095,
      size: '2',
      zipCode: undefined,
      city: undefined,
      street: undefined,
      houseNumber: undefined,
      gridOperator: undefined,
      gridOperatorId: undefined,
      gridAreaId: undefined,
      bookingLink: null,
      selfLink: null,
      calculationMode: null,
      customerType: CustomerType.Home,
      setCustomerType: (value: CustomerType) => {
        const { size, consumption } = get();
        const defaults = CustomerTypeDefaults[value];
        const isSizeNumeric = /\d/.test(size as string);
        const isHomeCustomer = value == CustomerType.Home;

        set(
          {
            customerType: value,
            size: isHomeCustomer || isSizeNumeric ? defaults.size : size,
            consumption:
              isHomeCustomer || (consumption && isSizeNumeric)
                ? defaults.consumption
                : consumption,
          },

          undefined,
          'energyCalculator/setCustomerType'
        );
      },

      setCalculatorData: (value: CalculatorData, mode: ReferenceTariffType) => {
        set(
          {
            ...value,
            calculationMode: mode,
          },
          undefined,
          'energyCalculator/setCalculatorData'
        );
      },
    }))
  )
);

export const calculationSelector = (state: EnergyCalculatorState) => ({
  consumption: state.consumption,
  size: state.size,
  zipCode: state.zipCode,
  city: state.city,
  street: state.street,
  houseNumber: state.houseNumber,
  gridOperatorId: state.gridOperatorId,
  gridAreaId: state.gridAreaId,
  calculationMode: state.calculationMode,
});

export const mapStateToEnergyData = (state: EnergyCalculatorState) => ({
  consumption: state.consumption,
  size: state.size,
  zipCode: state.zipCode,
  city: state.city,
  street: state.street,
  houseNumber: state.houseNumber,
  gridOperator:
    state.gridOperatorId != null
      ? {
          id: state.gridOperatorId,
          name: state.gridOperator,
          gridAreaId: state.gridAreaId,
        }
      : null,
});

export default useEnergyCalculatorStore;
